import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Row, Col, Card, Badge, Spinner, Alert, Button, Carousel } from 'react-bootstrap';
import HTMLView from '../components/Layout/HTMLView';
import ShareComponent from '../components/Common/ShareComponent';
import AddToCalendarComponent from '../components/Common/AddToCalendarComponent';
import { fetchSingleNews } from '../services/api';

const News = () => {
    const { id } = useParams();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                setLoading(true);
                const response = await fetchSingleNews(id)              
                setNews(response);
                setError(null);
            } catch (err) {
                console.error('Error fetching news:', err);
                setError('Failed to load news details. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchNews();
        }
    }, [id]);

    const LOCALE = 'en-GB';

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Etc/GMT' };
        return new Date(dateString).toLocaleDateString(LOCALE, options);
    };

    if (loading) {
        return (
            <div className="text-center my-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-2">Loading news details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="my-4">
                <Alert.Heading>Error</Alert.Heading>
                <p>{error}</p>
                <div className="d-flex justify-content-end">
                    <Button variant="outline-danger" onClick={() => window.history.back()}>
                        Go Back
                    </Button>
                </div>
            </Alert>
        );
    }

    if (!news) {
        return (
            <Alert variant="warning" className="my-4">
                <Alert.Heading>News Not Found</Alert.Heading>
                <p>The news you're looking for doesn't exist or has been removed.</p>
                <div className="d-flex justify-content-end">
                    <Link to="/" className="btn btn-outline-primary">
                        Return to Home
                    </Link>
                </div>
            </Alert>
        );
    }

    return (
        <div className="news-page py-5">
            <div className="container">
                {/* News Header */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h1 className="mb-0">{news.title}</h1>
                        {news.category && (
                            <Badge bg="info">
                                {news.category}
                            </Badge>
                        )}
                    </div>
                    <p className="text-muted">
                        <i className="bi bi-calendar-news me-2"></i>
                        {formatDate(news.createdAt || news.updatedAt)}
                        {/* {news.location && (
                            <>
                                <span className="mx-2">|</span>
                                <i className="bi bi-geo-alt-fill me-1"></i>
                                {news.location}
                            </>
                        )} */}
                    </p>
                </div>

                <Row className="g-4">
                    {/* Main News Information */}
                    <Col md={8}>
                        <Card className="mb-4">
                            <Card.Header className="bg-conference-primary">
                                <h3 className="h5 mb-0 text-white">News Details</h3>
                            </Card.Header>
                            <Card.Body>
                                {news.content && (
                                    <HTMLView 
                                        description={news.content} 
                                        files={news.files}
                                        endpoint="news"
                                    />
                                )}
                                
                                {!news.content && news.description && (
                                    <HTMLView 
                                        description={news.description} 
                                        files={news.files}
                                        endpoint="news"
                                    />
                                )}
                            </Card.Body>
                        </Card>

                        {/* Additional Details Section */}
                        {news.details && (
                            <Card className="mb-4">
                                <Card.Header className="bg-conference-primary">
                                    <h3 className="h5 mb-0 text-white">Additional Information</h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={news.details} 
                                        files={news.files}
                                        endpoint="news"
                                    />
                                </Card.Body>
                            </Card>
                        )}
                    </Col>

                    {/* Sidebar */}
                    <Col md={4}>
                        {/* News Media */}
                        {news.featuredImage && (
                            <Card className="mb-4">
                                <Card.Img 
                                    variant="top" 
                                    src={`${process.env.REACT_APP_API_URL}/news/files/${news.featuredImage}`}
                                    alt={news.title}
                                />
                            </Card>
                        )}
                        
                        {/* News Files */}
                        <Card className="mb-4">
                            <Card.Header className="bg-conference-secondary">
                                    <h3 className="h5 mb-0 text-white">News Materials</h3>
                            </Card.Header>
                            <Card.Body>
                                {news.files && news.files.length > 0 ? (
                                    <>
                                        {/* Image Slideshow */}
                                        {(() => {
                                            const imageFiles = news.files.filter(file => 
                                                file.mimetype && file.mimetype.startsWith('image/')
                                            );
                                            
                                            if (imageFiles.length > 0) {
                                                return (
                                                    <div className="mb-4">
                                                        <h5 className="mb-3">Images</h5>
                                                        <Carousel>
                                                            {imageFiles.map((file, index) => (
                                                                <Carousel.Item key={index}>
                                                                    <img
                                                                        className="d-block w-100"
                                                                        src={`${process.env.REACT_APP_API_URL}/news/files/${file.filename}`}
                                                                        alt={file.originalName || file.filename}
                                                                        style={{ maxHeight: '400px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                                                                    />
                                                                    <Carousel.Caption style={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: '10px' }}>
                                                                        <p className="mb-0">{file.originalName || file.filename}</p>
                                                                    </Carousel.Caption>
                                                                </Carousel.Item>
                                                            ))}
                                                        </Carousel>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                        
                                        {/* Other Files for Download */}
                                        {(() => {
                                            const otherFiles = news.files.filter(file => 
                                                !file.mimetype || !file.mimetype.startsWith('image/')
                                            );
                                            
                                            if (otherFiles.length > 0) {
                                                return (
                                                    <div>
                                                        <h5 className="mb-3">Downloads</h5>
                                                        <ul className="list-group list-group-flush">
                                                            {otherFiles.map((file, index) => (
                                                                <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                                                    <div>
                                                                        <i className="bi bi-file-earmark me-2"></i>
                                                                        {file.originalName || file.filename}
                                                                    </div>
                                                                    <a 
                                                                        href={`${process.env.REACT_APP_API_URL}/news/files/${file.filename}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                    >
                                                                        <i className="bi bi-download me-1"></i> Download
                                                                    </a>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </>
                                ) : (
                                    <p className="text-muted mb-0">No files available for this news.</p>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Actions */}
                        <Card className="mb-4">
                            <Card.Header className="bg-conference-secondary">
                                <h3 className="h5 mb-0 text-white">Actions</h3>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-grid gap-2">
                                    {news.newsDate && new Date(news.newsDate) > new Date() && (
                                        <Button variant="primary">
                                            <i className="bi bi-calendar-plus me-2"></i>Register
                                        </Button>
                                    )}
                                    {/* <AddToCalendarComponent
                                        title={news.title}
                                        description={news.description || news.content}
                                        startDate={news.eventDate || news.publishDate}
                                        endDate={news.eventEndDate}
                                        location={news.location}
                                        type="event"
                                    /> */}
                                    <ShareComponent
                                        title={news.title}
                                        description={`Check out this news: ${news.title}`}
                                        type="news"
                                    />
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Contact Information */}
                        {news.contactInfo && (
                            <Card className="mb-4">
                                <Card.Header className="bg-info bg-gradient text-white">
                                    <h3 className="h5 mb-0">Contact Information</h3>
                                </Card.Header>
                                <Card.Body>
                                    <HTMLView 
                                        description={news.contactInfo} 
                                        files={[]}
                                        endpoint="news"
                                    />
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>

                {/* Back Button */}
                <div className="mt-4">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => window.history.back()}
                    >
                        <i className="bi bi-arrow-left me-2"></i>Back
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default News;